
<script>
import TextInput from "@/components/TextInput";
import Message from "@/components/ErrorMessage";
import Button from "@/components/Button";
import TownSelection from "@/components/TownSelection";
import Captcha from "@/components/Captcha";
import bus from "@/bus";
import IBAN from "@/views/lang-specific/IBAN";
import features from "@/configs/features";
import { useUserStore } from "../../stores/user";
import { useTownSelectionStore } from "../../stores/townSelection";
import { useI18n } from "vue-i18n";

const statusCodes = {
  500: "views.modals.signup.500",
};

export default {
  setup() {
    const userStore = useUserStore();
    const townSelectionStore = useTownSelectionStore();
    const { t } = useI18n();
    return {
      userStore,
      townSelectionStore,
      t,
    };
  },
  name: "Signup",
  components: {
    Button,
    TextInput,
    Message,
    TownSelection,
    Captcha,
    IBAN,
  },
  data() {
    return {
      firstname: "",
      lastname: "",
      phone: "",
      email: "",
      password: "",
      password2: "",
      postHeld: "",
      message: null,
      registerState: 0,
      valid: true,
      confidentialityChecked: !features.NDA_CHECK,
    };
  },
  computed: {
    features: () => features,
    registerButton() {
      // loginState values:
      // 0: Not registered  yet
      // 1: Registering
      if (this.registerState === 1) {
        return {
          icon: "spinner",
          text: "views.modals.signup.state.1",
          color: "gray-700",
        };
      }
      return {
        icon: "sign-in-alt",
        text: "views.modals.signup.state.2",
        color: "airbnbRed",
      };
    },
  },
  methods: {
    isAlphaNum(string) {
      const re = /^[a-zA-Z0-9]*$/;
      return re.test(string);
    },
    async register() {
      if (this.password !== this.password2) {
        this.message = {
          id: new Date().toString(),
          status: null,
          message: "views.modals.signup.errors.1",
        };
        return;
      }
      if (features.VERSION === "it") {
        const regex = new RegExp(features.EXLUDED_MAIL);
        if (regex.exec(this.email) !== null) {
          this.message = {
            id: new Date().toString(),
            status: null,
            message: "views.modals.signup.errors.4",
          };
          return;
        }
      }

      const towns = this.townSelectionStore.ids;

      if (towns.length < 1) {
        this.message = {
          id: new Date().toString(),
          status: 500,
          message: "views.modals.signup.errors.2",
        };
        return;
      }

      const { firstname, lastname, phone, email, password, valid, postHeld } =
        this;
      let payload = {
        firstname,
        lastname,
        phone,
        email,
        password,
        valid,
        towns,
        ...(features.VERSION === "it" ? { postHeld } : {}),
      };
      if (features.IBAN) {
        const {
          beneficiaryName,
          beneficiaryAddress,
          beneficiaryCity,
          bankName,
          swiftRoutingNumber,
          ibanNumber,
          zipOfOrganization,
          istat,
        } = this.$refs.iban;

        payload = {
          beneficiaryName,
          beneficiaryAddress,
          beneficiaryCity,
          bankName,
          swiftRoutingNumber,
          ibanNumber,
          zipOfOrganization,
          istat,
          ...payload,
        };

        if (!this.isAlphaNum(ibanNumber)) {
          this.message = {
            id: new Date().toString(),
            status: 500,
            message: "views.modals.signup.errors.3",
          };
          return;
        }
      }

      this.registerState = 1;

      const error = await this.userStore.register(null, payload);
      if (!error) {
        this.townSelectionStore.clearTowns();
        this.message = {
          status: 200,
          message: "views.modals.signup.done",
        };
        this.registerState = 0;
        return;
      }
      const {
        status,
        statusText,
        data: { message },
      } = error;
      this.message = {
        id: new Date().toString(),
        status,
        message: statusCodes[status] || message || statusText,
      };
    },
    closeMessage(id) {
      Object.keys(this.errors).forEach((key) => {
        if (id === this.errors[key].id) {
          this.errors.splice(key, 1);
        }
      });
    },
    openLogin() {
      bus.emit("openModal", {
        modal: "login",
      });
    },
  },
};
</script>


<template>
  <div class="flex-1 p-5">
    <h1 class="mb-6 text-3xl font-light text-gray-700">
      {{ $t("views.modals.signup.title") }}
    </h1>
    <p
      v-for="line in $t('views.modals.signup.info').split('\n')"
      :key="line"
      class="mb-4 text-sm text-lightGrey"
    >
      {{ line }}
    </p>
    <Message v-if="message" :value="message" @input="message = null" />
    <form data-cy="signupForm" @submit.prevent="register">
      <div class="w-full flex flex-row space-around mb-4">
        <TextInput
          id="nom"
          v-model="lastname"
          data-cy="lastname"
          :label="$t('views.modals.signup.last_name')"
          :placeholder="$t('views.modals.signup.placeholders.last_name')"
          type="text"
          class="pr-1"
        />
        <TextInput
          id="prenom"
          v-model="firstname"
          data-cy="firstname"
          :label="$t('views.modals.signup.first_name')"
          :placeholder="$t('views.modals.signup.placeholders.first_name')"
          type="text"
          class="pl-1"
          :class="[features.VERSION === 'it' ? 'pr-1' : '']"
        />
        <TextInput
          v-if="features.VERSION === 'it'"
          id="postHeld"
          v-model="postHeld"
          data-cy="postHeld"
          :label="$t('views.modals.signup.postHeld')"
          :placeholder="$t('views.modals.signup.placeholders.postHeld')"
          type="text"
          class="pl-1"
        />
      </div>
      <div class="w-full flex flex-row space-around mb-4">
        <TextInput
          id="mail"
          v-model="email"
          data-cy="email"
          :label="$t('views.modals.signup.email')"
          :placeholder="$t('views.modals.signup.placeholders.email')"
          type="email"
          class="pr-1"
        />
        <TextInput
          id="telephone"
          v-model="phone"
          data-cy="phone"
          :label="$t('views.modals.signup.phone')"
          :placeholder="$t('views.modals.signup.placeholders.phone')"
          type="tel"
          class="pl-1"
        />
      </div>
      <div class="w-full flex flex-row space-around mb-4">
        <TextInput
          id="pass1"
          v-model="password"
          data-cy="password"
          :label="$t('views.modals.signup.password')"
          :placeholder="$t('views.modals.signup.placeholders.password')"
          type="password"
          class="pr-1"
        />
        <TextInput
          id="pass2"
          v-model="password2"
          data-cy="passwordConfirm"
          :label="$t('views.modals.signup.confirm_password')"
          :placeholder="$t('views.modals.signup.placeholders.confirm_password')"
          type="password"
          class="pl-1"
        />
      </div>
      <TownSelection :init-epci-mode="true" :init-expanded="true" />
      <IBAN
        v-if="features.IBAN"
        ref="iban"
        @checked="confidentialityChecked = !confidentialityChecked"
      />
      <div v-for="line in $tm('views.modals.signup.note')" :key="line">
        <!-- eslint-disable-next-line vue/no-v-html -->
        <p class="text-sm text-lightGrey mb-2" v-html="line" />
      </div>
      <Captcha :v-model="valid" />
      <div class="w-full flex items-center flex-row-reverse">
        <Button
          ref="buttonSumnit"
          class="text-sm"
          :bg="registerButton.color"
          :disabled="!confidentialityChecked"
          type="submit"
          data-cy="registerButton"
        >
          <font-awesome-icon
            :icon="registerButton.icon"
            class="mr-1"
            :spin="registerState === 1"
          />
          <span>{{ $t(registerButton.text) }}</span>
        </Button>
      </div>
    </form>
  </div>
</template>
